import React, { useEffect, useState } from "react";

import {
    Grid,
    Button,
} from "@mui/material";

import { Bar, Doughnut } from "react-chartjs-2";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";

import { FlaskConical, TimerOff, TimerReset } from "lucide-react";
import { getTrialReportAction } from "../../Actions/ReportsAction";
import CustomSnackbar from "../Common/snackBar";
import FilterDrawer from "../../components/DataTable/FilterDrawer";
import DataTable from "../../components/DataTable/DataTable";
import { TableFilte } from "../../Assets/svg/svgicon";
import ReportKpiCards from "./ReportKpiCards";
import {
    barChartOptions,
    barSeries,
    ChartLegend,
    ChartPanel,
    legendFromChartData,
    portalColor,
    ringCenterPlugin,
    ringChartOptions,
    ringSeries,
    statusColor,
} from "./reportCharts";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const formatTrialDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? value
        : date.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
};

const trialColumns = [
    {
        field: "tenant",
        headerName: "Tenant",
        minWidth: 190,
        renderCell: ({ row }) => row.tenant || "—",
    },
    {
        field: "product",
        headerName: "Product",
        minWidth: 130,
        renderCell: ({ row }) => (
            <span className="trial-product-name">{row.product || "—"}</span>
        ),
    },
    {
        field: "trialStart",
        headerName: "Trial Start",
        minWidth: 180,
        renderCell: ({ row }) => (
            <span className="trial-date">{formatTrialDate(row.trialStart)}</span>
        ),
    },
    {
        field: "trialEnd",
        headerName: "Trial End",
        minWidth: 180,
        renderCell: ({ row }) => (
            <span className="trial-date">{formatTrialDate(row.trialEnd)}</span>
        ),
    },
    {
        field: "status",
        headerName: "Status",
        minWidth: 110,
        renderCell: ({ row }) => {
            const isActive = String(row.status).toLowerCase() === "active";
            return (
                <span className={`trial-status ${isActive ? "is-active" : "is-expired"}`}>
                    {row.status || "—"}
                </span>
            );
        },
    },
];

const drawerFilters = [
    {
        name: "product",
        label: "Portal",
        type: "select",
        defaultValue: "all",
        options: [
            { label: "All Portals", value: "all" },
            { label: "QBot", value: "qbot" },
            { label: "PayCentral", value: "paycentral" },
            { label: "KYC", value: "kyc" },
            { label: "Tripola", value: "tripola" },
        ],
    },
    {
        name: "status",
        label: "Status",
        type: "status",
        defaultValue: "all",
        options: [
            { label: "All Status", value: "all" },
            { label: "Active", value: "active" },
            { label: "Expired", value: "expired" },
        ],
    },
    {
        name: "dateRange",
        label: "Date Range",
        type: "dateRange",
        placeholder: "Select date range",
    },
];

const TrialReports = () => {
    const [loading, setLoading] = useState(false);

    const getDefaultDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        return { from, to };
    };

    const [filterValues, setFilterValues] = useState({
        product: "all",
        status: "all",
        dateRange: getDefaultDateRange(),
    });

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [summary, setSummary] = useState({
        totalTrials: 0,
        activeTrials: 0,
        expiredTrials: 0,
    });

    const [chartData, setChartData] = useState({
        status: { active: 0, expired: 0 },
        productCount: { qbot: 0, paycentral: 0, kyc: 0, tripola: 0 },
    });

    const [trialList, setTrialList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const buildApiFilters = (fv) => ({
        product: fv.product === "all" ? "" : fv.product,
        status: fv.status === "all" ? "" : fv.status,
        startDate: fv.dateRange?.from ? fv.dateRange.from.toISOString().split("T")[0] : "",
        endDate: fv.dateRange?.to ? fv.dateRange.to.toISOString().split("T")[0] : "",
    });

    const fetchTrialReport = async (fv = filterValues) => {
        try {
            setLoading(true);
            const response = await getTrialReportAction(buildApiFilters(fv));
            if (response?.status === 200) {
                setSummary(response.data.summary);
                setChartData(response.data.charts);
                setTrialList(response.data.trials || []);
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTrialReport();
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterApply = (newValues) => {
        setFilterValues(newValues);
        setCurrentPage(1);
        fetchTrialReport(newValues);
    };

    const handleFilterReset = () => {
        const resetValues = { product: "all", status: "all", dateRange: getDefaultDateRange() };
        setFilterValues(resetValues);
        setCurrentPage(1);
        fetchTrialReport(resetValues);
    };

    const hasAppliedFilters =
        filterValues.product !== "all" ||
        filterValues.status !== "all";

    const statusChartData = {
        labels: ["Active", "Expired"],
        datasets: [
            ringSeries({
                data: [chartData?.status?.active || 0, chartData?.status?.expired || 0],
                colors: [statusColor("active"), statusColor("expired")],
            }),
        ],
    };

    const productChartData = {
        labels: ["QBot", "PayCentral", "KYC", "Tripola"],
        datasets: [
            barSeries({
                label: "Trial Count",
                data: [
                    chartData?.productCount?.qbot || 0,
                    chartData?.productCount?.paycentral || 0,
                    chartData?.productCount?.kyc || 0,
                    chartData?.productCount?.tripola || 0,
                ],
                colors: [
                    portalColor("qbot"),
                    portalColor("paycentral"),
                    portalColor("kyc"),
                    portalColor("tripola"),
                ],
            }),
        ],
    };

    const hasStatus =
        Number(chartData?.status?.active || 0) + Number(chartData?.status?.expired || 0) > 0;

    const trialRows = trialList.map((item, index) => ({
        ...item,
        _rowId: `${item.tenant || "tenant"}-${item.product || "product"}-${item.trialStart || index}-${index}`,
    }));
    const paginatedTrialRows = trialRows.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <div className="card border-rounded-0 fiter-card">
                        <div className="card-body p-5 pb-0">
                            <div className="row align-items-center mb-5">
                                <div className="col-auto">
                                    <h2 className="chart-title mb-0">Trial Report</h2>
                                </div>

                                <div className="col-auto ms-auto d-flex gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TableFilte />}
                                        className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
                                    >
                                        Filters
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>

                <Grid item xs={12}>
                    <ReportKpiCards
                        loading={loading}
                        cards={[
                            {
                                key: "total",
                                label: "Total Trials",
                                value: summary.totalTrials ?? 0,
                                accent: "#22419e",
                                icon: <FlaskConical />,
                            },
                            {
                                key: "active",
                                label: "Active Trials",
                                value: summary.activeTrials ?? 0,
                                accent: "#059669",
                                icon: <TimerReset />,
                            },
                            {
                                key: "expired",
                                label: "Expired Trials",
                                value: summary.expiredTrials ?? 0,
                                accent: "#dc2626",
                                icon: <TimerOff />,
                            },
                        ]}
                    />
                </Grid>

                {/* CHARTS */}
                <Grid container spacing={3} mt={1} sx={{ width: "100%", marginLeft: 0 }}>
                    <Grid item xs={12} md={8} sx={{ display: "flex" }}>
                        <ChartPanel
                            title="Trials by product"
                            caption="Trial count started per product"
                            height={330}
                            loading={loading}
                        >
                            <Bar data={productChartData} options={barChartOptions()} />
                        </ChartPanel>
                    </Grid>

                    <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                        <ChartPanel
                            title="Trial status"
                            caption="Active against expired trials"
                            height={330}
                            loading={loading}
                            isEmpty={!hasStatus}
                        >
                            <div className="report-ring">
                                <div className="report-ring-chart">
                                    <Doughnut
                                        data={statusChartData}
                                        options={{
                                            ...ringChartOptions(),
                                            plugins: {
                                                ...ringChartOptions().plugins,
                                                reportRingCenter: { caption: "Trials" },
                                            },
                                        }}
                                        plugins={[ringCenterPlugin]}
                                    />
                                </div>
                                <ChartLegend items={legendFromChartData(statusChartData)} />
                            </div>
                        </ChartPanel>
                    </Grid>

                    <Grid item xs={12}>
                        <div className="trial-details-table">
                            <DataTable
                                columns={trialColumns}
                                rows={paginatedTrialRows}
                                loading={loading}
                                totalCount={trialRows.length}
                                page={currentPage}
                                pageSize={pageSize}
                                getRowId={(row) => row._rowId}
                                header={(
                                    <div className="trial-details-heading">
                                        <h3>Trial details</h3>
                                        <p>Tenant-level trial periods and current status</p>
                                    </div>
                                )}
                                onPageChange={setCurrentPage}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
            </Grid>

            {/* Filter Drawer */}
            <FilterDrawer
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                filters={drawerFilters}
                filterValues={filterValues}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default TrialReports;